import cx from 'classnames'
import { useKeenSlider } from 'keen-slider/react'
import { useState } from 'react'

import { type GalleryCarouselItem, WheelControls } from '@lib/gallery'
import { screens } from '@lib/theme'

import GalleryCarouselControls from './controls'
import GalleryCarouselSlide from './slide'
import GalleryCarouselThumbnail from './thumbnail'

export interface GalleryCarouselProps {
  id?: string
  items: GalleryCarouselItem[]
  hasArrows?: boolean
  hasDots?: boolean
  hasCounter?: boolean
  hasDrag?: boolean
  hasThumbnails?: boolean
  cornerControls?: boolean
  preloadFirstImage?: boolean
  className?: string
}

const GalleryCarousel = ({
  id,
  items,
  hasArrows,
  hasDots,
  hasCounter,
  hasDrag = true,
  hasThumbnails,
  cornerControls,
  preloadFirstImage,
  className,
}: GalleryCarouselProps) => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    selector: '.carousel-slide',
    defaultAnimation: {
      duration: 800,
    },
    dragSpeed: 0.8,
    drag: hasDrag,
    slideChanged(slider) {
      const index = slider.track.details.rel
      setCurrentSlide(index)
      thumbnailSlider.current?.moveToIdx(index)
    },
  })

  const [thumbnailRef, thumbnailSlider] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      selector: '.carousel-thumbnail',
      mode: 'free',
      defaultAnimation: {
        duration: 800,
      },
      drag: true,
      slides: {
        spacing: 8,
        perView: 'auto',
      },
      breakpoints: {
        [`(min-width: ${screens.md})`]: {
          vertical: true,
          rubberband: false,
        },
      },
    },
    [WheelControls],
  )

  return (
    <div
      id={id}
      className={cx(
        {
          'flex flex-col gap-2 relative md:pr-[calc(6rem+8px)] overflow-hidden':
            hasThumbnails,
        },
        className,
      )}
    >
      <div className="relative overflow-hidden grow-0">
        <div
          ref={sliderRef}
          className={cx(
            'flex relative overflow-hidden will-change-transform touch-action touch-action-pan-y',
            {
              'cursor-grab active:cursor-grabbing': hasDrag,
            },
          )}
        >
          {items.map((item, index) => (
            <GalleryCarouselSlide
              key={`slide-${item.id}`}
              index={index}
              item={item}
              preloadFirstImage={preloadFirstImage}
            />
          ))}
        </div>

        {(hasArrows || hasDots) && items.length > 1 && (
          <GalleryCarouselControls
            slider={slider.current}
            currentSlide={currentSlide}
            slideCount={items.length}
            id={id}
            hasArrows={hasArrows}
            hasDots={hasDots}
            hasCounter={hasCounter}
            cornerControls={cornerControls}
          />
        )}
      </div>

      {hasThumbnails && (
        <div
          ref={thumbnailRef}
          className="flex md:absolute md:right-0 md:inset-y-0 md:flex-col"
        >
          {items.map((item, index) => (
            <GalleryCarouselThumbnail
              key={`thumbnail-${item.id}`}
              item={item}
              isSelected={index === currentSlide}
              onClick={() => {
                setCurrentSlide(index)
                slider.current?.moveToIdx(index)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default GalleryCarousel
